import { useEffect, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import {
  Users,
  User,
  UserCheck,
  TrendingUp,
  AlertCircle,
  DollarSign,
  Clock,
  BarChart3,
  Pill,
  CreditCard,
  Tag,
} from 'lucide-react';
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  BarChart,
  Bar,
  Legend,
} from 'recharts';
import MetricsCard from '../MetricsCard';
import { getDefaultContactValue } from '@/lib/utils';
import clinicService, { PaymentOverview, DashboardMetrics, DashboardGrowthData, DashboardActivity } from '@/services/clinicService';
import { useAuth } from '@/contexts/AuthContext';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { useClinic } from '@/contexts/ClinicContext';
import { TaskPanel } from '@/components/tasks';

interface ActivityItem {
  id: string;
  action: string;
  entity: string;
  time: string;
  type: 'patient' | 'practitioner' | 'prescription' | 'payment' | 'price_change' | 'staff';
}

const ClinicDashboard = () => {
  const { user } = useAuth();
  const { clinic, isGuided } = useClinic();
  
  // Individual loading states for each section
  const [metricsLoading, setMetricsLoading] = useState(true);
  const [revenueLoading, setRevenueLoading] = useState(true);
  const [growthLoading, setGrowthLoading] = useState(true);
  const [patientsLoading, setPatientsLoading] = useState(true);
  const [activityLoading, setActivityLoading] = useState(true);
  
  const [error, setError] = useState<string | null>(null);
  const [timePeriod, setTimePeriod] = useState('thisYear');
  
  // Data states
  const [metrics, setMetrics] = useState<DashboardMetrics & { 
    monthlyRevenue: number; 
    totalRevenue: number;
    revenueGrowth: number;
    patientChange: number;
    practitionerChange: number;
    productChange: number;
  }>({
    totalPatients: 0,
    activePractitioners: 0,
    totalProducts: 0,
    monthlyRevenue: 0,
    totalRevenue: 0,
    revenueGrowth: 0,
    patientChange: 0,
    practitionerChange: 0,
    productChange: 0,
  });
  const [paymentOverview, setPaymentOverview] = useState<PaymentOverview | null>(null);
  const [growthData, setGrowthData] = useState<DashboardGrowthData[]>([]);
  const [recentActivity, setRecentActivity] = useState<ActivityItem[]>([]);
  const [recentPatients, setRecentPatients] = useState<any[]>([]);

  // Check if initial load is complete (metrics loaded)
  const isInitialLoading = metricsLoading;

  useEffect(() => {
    if (clinic?.id) {
      fetchAllDashboardData(clinic?.id);
    }
  }, [clinic?.id]);

  // Refetch revenue when time period changes
  useEffect(() => {
    if (clinic?.id) {
      fetchRevenueData(clinic?.id);
    }
  }, [timePeriod]);

  const fetchAllDashboardData = async (clinicId: string) => {
    setError(null);
    
    // Fetch all data in parallel - each with its own loading state
    fetchMetricsData(clinicId);
    fetchRevenueData(clinicId);
    fetchGrowthData(clinicId);
    fetchPatientsData(clinicId);
    fetchActivityData(clinicId);
  };

  const fetchMetricsData = async (clinicId: string) => {
    try {
      setMetricsLoading(true);
      const [metricsResponse, paymentResponse] = await Promise.all([
        clinicService.getDashboardMetrics(clinicId),
        clinicService.getPaymentOverview(clinicId, timePeriod, '', ''),
      ]);
      
      // Get current month revenue from revenueData array
      const currentMonthIndex = new Date().getMonth(); // 0-11
      const currentMonthRevenue = paymentResponse.revenueData?.[currentMonthIndex]?.amount || 0;
      const lastMonthRevenue = currentMonthIndex > 0 
        ? (paymentResponse.revenueData?.[currentMonthIndex - 1]?.amount || 0)
        : 0;
      
      // Calculate month-over-month revenue change
      const revenueChange = lastMonthRevenue === 0 
        ? (currentMonthRevenue > 0 ? 100 : 0)
        : ((currentMonthRevenue - lastMonthRevenue) / lastMonthRevenue) * 100;

      setMetrics({
        totalPatients: metricsResponse.totalPatients || 0,
        activePractitioners: metricsResponse.activePractitioners || 0,
        totalProducts: metricsResponse.totalProducts || 0,
        monthlyRevenue: currentMonthRevenue,
        totalRevenue: paymentResponse.metrics?.totalRevenue || 0,
        revenueGrowth: Math.round(revenueChange * 100) / 100,
        patientChange: metricsResponse.patientChange || 0,
        practitionerChange: metricsResponse.practitionerChange || 0,
        productChange: metricsResponse.productChange || 0,
      });
    } catch (err: any) {
      console.error('Error fetching metrics:', err);
    } finally {
      setMetricsLoading(false);
    }
  };

  const fetchRevenueData = async (clinicId: string) => {
    try {
      setRevenueLoading(true);
      const paymentResponse = await clinicService.getPaymentOverview(clinicId, timePeriod, '', '');
      setPaymentOverview(paymentResponse);
      
      // Get current month revenue from revenueData array
      const currentMonthIndex = new Date().getMonth(); // 0-11
      const currentMonthRevenue = paymentResponse.revenueData?.[currentMonthIndex]?.amount || 0;
      const lastMonthRevenue = currentMonthIndex > 0 
        ? (paymentResponse.revenueData?.[currentMonthIndex - 1]?.amount || 0)
        : 0;
      
      // Calculate month-over-month revenue change
      const revenueChange = lastMonthRevenue === 0 
        ? (currentMonthRevenue > 0 ? 100 : 0)
        : ((currentMonthRevenue - lastMonthRevenue) / lastMonthRevenue) * 100;

      // Update revenue metrics
      setMetrics(prev => ({
        ...prev,
        monthlyRevenue: currentMonthRevenue,
        totalRevenue: paymentResponse.metrics?.totalRevenue || 0,
        revenueGrowth: Math.round(revenueChange * 100) / 100,
      }));
    } catch (err: any) {
      console.error('Error fetching revenue:', err);
    } finally {
      setRevenueLoading(false);
    }
  };

  const fetchGrowthData = async (clinicId: string) => {
    try {
      setGrowthLoading(true);
      const growthResponse = await clinicService.getDashboardGrowth(clinicId, 6);
      setGrowthData(growthResponse.growthData || []);
    } catch (err: any) {
      console.error('Error fetching growth data:', err);
    } finally {
      setGrowthLoading(false);
    }
  };

  const fetchPatientsData = async (clinicId: string) => {
    try {
      setPatientsLoading(true);
      const patientsResponse = await clinicService.getPatients(1, 5, '', [clinicId]);
      setRecentPatients(patientsResponse.patients || []);
    } catch (err: any) {
      console.error('Error fetching patients:', err);
    } finally {
      setPatientsLoading(false);
    }
  };

  const fetchActivityData = async (clinicId: string) => {
    try {
      setActivityLoading(true);
      // Limit to 5 entries from API
      const activityResponse = await clinicService.getDashboardActivity(clinicId, 7, 5);
      
      // Transform API response to ActivityItem format
      const activities: ActivityItem[] = (activityResponse.activities || []).map((item) => ({
        id: item.id,
        action: item.action,
        entity: item.entity || 'Unknown',
        time: formatTimeAgo(item.timestamp),
        type: item.type as ActivityItem['type'],
      }));
      
      setRecentActivity(activities);
    } catch (err: any) {
      console.error('Error fetching activity:', err);
    } finally {
      setActivityLoading(false);
    }
  };

  const formatTimeAgo = (dateString: string): string => {
    const date = new Date(dateString);
    const now = new Date();
    const diffInMinutes = Math.floor((now.getTime() - date.getTime()) / (1000 * 60));

    if (diffInMinutes < 60) return `${diffInMinutes} minutes ago`;
    if (diffInMinutes < 1440) return `${Math.floor(diffInMinutes / 60)} hours ago`;
    return `${Math.floor(diffInMinutes / 1440)} days ago`;
  };

  const formatCurrency = (amount: number): string => {
    return `$${amount.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  };

  // Helper to format trend percentage
  const formatTrend = (change: number, hasData: boolean, noDataText: string) => {
    if (!hasData) return noDataText;
    if (change === 0) return '0%';
    const sign = change > 0 ? '+' : '';
    return `${sign}${change.toFixed(1)}%`;
  };

  // Metrics configuration matching admin design
  const dashboardMetrics = [
    {
      title: 'Total Patients',
      value: metricsLoading ? '...' : metrics.totalPatients,
      icon: Users,
      trend: formatTrend(metrics.patientChange, metrics.totalPatients > 0, 'No patients yet'),
      trendUp: metrics.patientChange >= 0,
      color: '#067A76',
    },
    {
      title: 'Active Practitioners',
      value: metricsLoading ? '...' : metrics.activePractitioners,
      icon: UserCheck,
      trend: formatTrend(metrics.practitionerChange, metrics.activePractitioners > 0, 'No practitioners yet'),
      trendUp: metrics.practitionerChange >= 0,
      color: '#BD6F51',
    },
    {
      title: 'Total Products',
      value: metricsLoading ? '...' : metrics.totalProducts,
      icon: Pill,
      trend: formatTrend(metrics.productChange, metrics.totalProducts > 0, 'No products yet'),
      trendUp: metrics.productChange >= 0,
      color: '#D4A853',
    },
    {
      title: 'Monthly Revenue',
      value: metricsLoading ? '...' : formatCurrency(metrics.monthlyRevenue),
      icon: DollarSign,
      trend: formatTrend(metrics.revenueGrowth, metrics.monthlyRevenue > 0, 'No revenue data'),
      trendUp: metrics.revenueGrowth >= 0,
      color: '#4A8A9A',
    },
  ];

  const getActivityBadgeColor = (type: string) => {
    switch (type) {
      case 'patient':
        return 'bg-brand-teal/10 text-brand-teal';
      case 'practitioner':
        return 'bg-brand-copper/10 text-brand-copper';
      case 'prescription':
        return 'bg-brand-gold/10 text-brand-gold';
      default:
        return 'bg-slate-100 text-slate-600';
    }
  };

  // Custom tooltip for charts
  const CustomTooltip = ({ active, payload, label }: { active?: boolean; payload?: any[]; label?: string }) => {
    if (active && payload && payload.length) {
      return (
        <div className="bg-white p-3 border border-slate-200 rounded-lg shadow-lg">
          <p className="font-medium text-slate-900">{label}</p>
          {payload.map((entry, index: number) => (
            <p key={index} style={{ color: entry.color }} className="text-sm">
              {entry.name}: {typeof entry.value === 'number' && entry.name.toLowerCase().includes('revenue') 
                ? `$${entry.value.toLocaleString()}` 
                : entry.value.toLocaleString()}
            </p>
          ))}
        </div>
      );
    }
    return null;
  };

  if (!clinic?.id) {
    return (
      <Alert className="rounded-lg">
        <AlertCircle className="h-4 w-4" />
        <AlertDescription>No clinic information available. Please contact support.</AlertDescription>
      </Alert>
    );
  }

  if (isInitialLoading) {
    return (
      <div className="space-y-4 sm:space-y-6">
        {/* Header Skeleton */}
        <div className="mb-2">
          <div className="h-6 sm:h-7 w-40 sm:w-52 bg-slate-200 rounded-md animate-pulse mb-2" />
          <div className="h-3 sm:h-4 w-56 sm:w-72 bg-slate-100 rounded animate-pulse" />
        </div>

        {/* Metric Cards Skeleton */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 sm:gap-4 md:gap-6">
          {[1, 2, 3, 4].map(index => (
            <Card key={index} className="border-slate-200">
              <CardContent className="p-4 sm:p-5">
                <div className="flex items-start justify-between">
                  <div className="space-y-3 flex-1">
                    <div className="h-3 w-20 bg-slate-100 rounded animate-pulse" />
                    <div className="h-7 sm:h-8 w-16 bg-slate-200 rounded animate-pulse" />
                    <div className="h-2.5 w-14 bg-slate-100 rounded animate-pulse" />
                  </div>
                  <div className="h-10 w-10 sm:h-11 sm:w-11 bg-slate-100 rounded-xl animate-pulse" />
                </div>
              </CardContent>
            </Card>
          ))}
        </div>

        {/* Charts Row 1 Skeleton */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4 md:gap-6">
          {/* Revenue Overview Skeleton */}
          <Card className="md:col-span-2 lg:col-span-3 border-slate-200">
            <CardHeader className="pb-2 px-4 sm:px-6">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="h-8 w-8 bg-emerald-50 rounded-lg animate-pulse" />
                  <div className="h-4 w-32 bg-slate-200 rounded animate-pulse" />
                </div>
                <div className="h-8 w-24 bg-slate-100 rounded-md animate-pulse" />
              </div>
            </CardHeader>
            <CardContent className="px-4 sm:px-6 pb-4">
              <div className="h-48 sm:h-56 bg-slate-50 rounded-lg animate-pulse flex items-end justify-around px-4 pb-4">
                {[40, 60, 45, 70, 55, 80, 65, 75, 50, 85, 70, 60].map((h, i) => (
                  <div key={i} className="w-4 sm:w-6 bg-slate-200 rounded-t animate-pulse" style={{ height: `${h}%` }} />
                ))}
              </div>
            </CardContent>
          </Card>

          {/* Growth Analytics Skeleton */}
          <Card className="md:col-span-2 lg:col-span-2 border-slate-200">
            <CardHeader className="pb-2 px-4 sm:px-6">
              <div className="flex items-center gap-3">
                <div className="h-8 w-8 bg-purple-50 rounded-lg animate-pulse" />
                <div className="h-4 w-32 bg-slate-200 rounded animate-pulse" />
              </div>
            </CardHeader>
            <CardContent className="px-4 sm:px-6 pb-4">
              <div className="h-48 sm:h-56 bg-slate-50 rounded-lg animate-pulse" />
            </CardContent>
          </Card>
        </div>

        {/* Charts Row 2 Skeleton */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4 md:gap-6">
          {/* Recent Patients Skeleton */}
          <Card className="md:col-span-2 lg:col-span-3 border-slate-200">
            <CardHeader className="pb-2 px-4 sm:px-6">
              <div className="flex items-center gap-3">
                <div className="h-8 w-8 bg-blue-50 rounded-lg animate-pulse" />
                <div className="h-4 w-28 bg-slate-200 rounded animate-pulse" />
              </div>
            </CardHeader>
            <CardContent className="px-4 sm:px-6">
              <div className="space-y-3">
                {[1, 2, 3, 4, 5].map(i => (
                  <div key={i} className="flex items-center gap-3 py-2 border-b border-slate-50">
                    <div className="h-4 w-4 bg-slate-100 rounded animate-pulse" />
                    <div className="h-9 w-9 bg-slate-100 rounded-xl animate-pulse" />
                    <div className="flex-1 space-y-1.5">
                      <div className="h-3.5 w-32 bg-slate-200 rounded animate-pulse" />
                    </div>
                    <div className="h-3 w-28 bg-slate-100 rounded animate-pulse hidden sm:block" />
                    <div className="h-5 w-14 bg-slate-100 rounded-full animate-pulse" />
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>

          {/* Recent Activity Skeleton */}
          <Card className="md:col-span-2 lg:col-span-2 border-slate-200">
            <CardHeader className="pb-3 px-4 sm:px-6">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="h-8 w-8 bg-orange-50 rounded-lg animate-pulse" />
                  <div className="h-4 w-28 bg-slate-200 rounded animate-pulse" />
                </div>
                <div className="h-3 w-16 bg-slate-100 rounded animate-pulse" />
              </div>
            </CardHeader>
            <CardContent className="px-4 sm:px-6">
              <div className="space-y-2">
                {[1, 2, 3, 4, 5].map(i => (
                  <div key={i} className="flex items-center gap-3 p-2 rounded-xl">
                    <div className="h-9 w-9 bg-slate-100 rounded-xl animate-pulse" />
                    <div className="flex-1 space-y-1.5">
                      <div className="h-3.5 w-28 bg-slate-200 rounded animate-pulse" />
                      <div className="h-2.5 w-20 bg-slate-100 rounded animate-pulse" />
                    </div>
                    <div className="space-y-1.5 text-right">
                      <div className="h-5 w-14 bg-slate-100 rounded-full animate-pulse" />
                      <div className="h-2.5 w-16 bg-slate-100 rounded animate-pulse" />
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4 sm:space-y-6">
      {/* Page Header */}
      <div className="mb-2">
        <h1 className="font-montserrat text-slate-900 tracking-tight text-lg sm:text-2xl" style={{ fontWeight: 700 }}>
          Dashboard Overview
        </h1>
        <p className="text-slate-600 font-inter mt-1 text-xs sm:text-sm">
          Welcome to your Nimbus clinic dashboard
        </p>
      </div>

      {error && (
        <Alert className="rounded-lg border-red-200 bg-red-50">
          <AlertCircle className="h-4 w-4 text-red-600" />
          <AlertDescription className="text-red-800">{error}</AlertDescription>
        </Alert>
      )}

      {/* Metrics Cards — hide Total Products and Active Practitioners for guided clinics */}
      <div className="grid grid-cols-2 sm:grid-cols-2 md:grid-cols-4 gap-3 sm:gap-4 md:gap-5">
        {dashboardMetrics
          .filter((metric) => !(isGuided && (metric.title === 'Total Products' || metric.title === 'Active Practitioners')))
          .map((metric, index) => (
            <MetricsCard key={index} {...metric} />
          ))}
      </div>

      {/* Charts Row 1: Revenue Trend & Growth Analytics */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4 md:gap-6">
        {/* Revenue Trend Chart */}
        <Card className="md:col-span-2 lg:col-span-3">
          <CardHeader className="pb-2 px-3 sm:px-6">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
              <CardTitle className="flex items-center text-slate-900 font-montserrat font-semibold text-sm sm:text-base">
                <div className="w-7 h-7 sm:w-8 sm:h-8 rounded-lg bg-emerald-50 flex items-center justify-center mr-2 sm:mr-3">
                  <TrendingUp className="h-3.5 w-3.5 sm:h-4 sm:w-4 text-emerald-600" strokeWidth={2} />
                </div>
                Revenue Overview
              </CardTitle>
              <select 
                className="text-xs sm:text-sm border border-slate-200 rounded-md px-2 py-1 text-slate-600 bg-white w-fit"
                value={timePeriod}
                onChange={(e) => setTimePeriod(e.target.value)}
              >
                <option value="thisYear">This Year</option>
                <option value="previousYear">Last Year</option>
              </select>
            </div>
          </CardHeader>
          <CardContent className="px-2 sm:px-6">
            {revenueLoading ? (
              <div className="h-[220px] sm:h-[280px] bg-slate-50 rounded-lg animate-pulse flex items-end justify-around px-4 pb-4">
                {[40, 60, 45, 70, 55, 80, 65, 75, 50, 85, 70, 60].map((h, i) => (
                  <div key={i} className="w-4 sm:w-6 bg-slate-200 rounded-t animate-pulse" style={{ height: `${h}%` }} />
                ))}
              </div>
            ) : (
              <div className="h-[220px] sm:h-[280px]">
                {paymentOverview?.revenueData?.length ? (
                  <ResponsiveContainer width="100%" height="100%">
                    <AreaChart 
                      data={paymentOverview.revenueData} 
                      margin={{ top: 10, right: 5, left: -15, bottom: 0 }}
                    >
                      <defs>
                        <linearGradient id="colorRevenue" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="5%" stopColor="#067A76" stopOpacity={0.3}/>
                          <stop offset="95%" stopColor="#067A76" stopOpacity={0}/>
                        </linearGradient>
                      </defs>
                      <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                      <XAxis 
                        dataKey="date"
                        axisLine={false} 
                        tickLine={false} 
                        tick={{ fontSize: 10, fill: '#64748b' }}
                        interval="preserveStartEnd"
                      />
                      <YAxis 
                        axisLine={false} 
                        tickLine={false} 
                        tick={{ fontSize: 10, fill: '#64748b' }}
                        tickFormatter={(value) => `$${(value / 1000).toFixed(0)}k`}
                        width={40}
                      />
                      <Tooltip content={<CustomTooltip />} />
                      <Area
                        type="monotone"
                        dataKey="amount"
                        stroke="#067A76"
                        strokeWidth={2}
                        fillOpacity={1}
                        fill="url(#colorRevenue)"
                        name="Revenue"
                      />
                    </AreaChart>
                  </ResponsiveContainer>
                ) : (
                  <div className="h-full flex items-center justify-center text-slate-400 text-sm">
                    No revenue data available
                  </div>
                )}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Growth Analytics Chart */}
        <Card className="md:col-span-2 lg:col-span-2">
          <CardHeader className="pb-2 px-3 sm:px-6">
            <div className="flex items-center justify-between">
              <CardTitle className="flex items-center text-slate-900 font-montserrat font-semibold text-sm sm:text-base">
                <div className="w-7 h-7 sm:w-8 sm:h-8 rounded-lg bg-purple-50 flex items-center justify-center mr-2 sm:mr-3">
                  <BarChart3 className="h-3.5 w-3.5 sm:h-4 sm:w-4 text-purple-600" strokeWidth={2} />
                </div>
                Growth Analytics
              </CardTitle>
            </div>
          </CardHeader>
          <CardContent className="px-2 sm:px-6">
            {growthLoading ? (
              <div className="h-[220px] sm:h-[280px] bg-slate-50 rounded-lg animate-pulse" />
            ) : (
              <div className="h-[220px] sm:h-[280px]">
                {growthData.length > 0 ? (
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={growthData} margin={{ top: 10, right: 5, left: -20, bottom: 0 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                      <XAxis 
                        dataKey="month" 
                        axisLine={false} 
                        tickLine={false}
                        tick={{ fontSize: 10, fill: '#64748b' }}
                      />
                      <YAxis 
                        axisLine={false} 
                        tickLine={false}
                        tick={{ fontSize: 10, fill: '#64748b' }}
                        width={40}
                        tickFormatter={(value) => value >= 1000 ? `${(value / 1000).toFixed(0)}k` : value.toString()}
                      />
                      <Tooltip content={<CustomTooltip />} />
                      <Legend 
                        wrapperStyle={{ fontSize: '11px' }}
                        iconType="circle"
                        iconSize={6}
                      />
                      <Bar dataKey="patients" fill="#067A76" radius={[4, 4, 0, 0]} name="Patients" />
                      {!isGuided && <Bar dataKey="practitioners" fill="#BD6F51" radius={[4, 4, 0, 0]} name="Practitioners" />}
                    </BarChart>
                  </ResponsiveContainer>
                ) : (
                  <div className="h-full flex items-center justify-center text-slate-400 text-sm">
                    No growth data available
                  </div>
                )}
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Charts Row 2: Recent Patients & Recent Activity */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4 md:gap-6">
        {/* Recent Patients Table */}
        <Card className="md:col-span-2 lg:col-span-3 border-slate-200">
          <CardHeader className="pb-2 px-3 sm:px-6">
            <CardTitle className="flex items-center text-slate-900 font-montserrat font-semibold text-sm sm:text-base">
              <div className="w-7 h-7 sm:w-8 sm:h-8 rounded-lg bg-blue-50 flex items-center justify-center mr-2 sm:mr-3">
                <Clock className="h-3.5 w-3.5 sm:h-4 sm:w-4 text-blue-600" strokeWidth={2} />
              </div>
              Recent Patients
            </CardTitle>
          </CardHeader>
          <CardContent className="px-2 sm:px-6">
            {patientsLoading ? (
              <div className="space-y-3">
                {[1, 2, 3, 4, 5].map(i => (
                  <div key={i} className="flex items-center gap-3 py-2 border-b border-slate-50">
                    <div className="h-4 w-4 bg-slate-100 rounded animate-pulse" />
                    <div className="h-9 w-9 bg-slate-100 rounded-xl animate-pulse" />
                    <div className="flex-1 space-y-1.5">
                      <div className="h-3.5 w-32 bg-slate-200 rounded animate-pulse" />
                    </div>
                    <div className="h-3 w-28 bg-slate-100 rounded animate-pulse hidden sm:block" />
                    <div className="h-5 w-14 bg-slate-100 rounded-full animate-pulse" />
                  </div>
                ))}
              </div>
            ) : recentPatients.length > 0 ? (
              <div className="overflow-x-auto -mx-2 sm:mx-0">
                <table className="w-full min-w-[400px]">
                  <thead>
                    <tr className="border-b border-slate-100">
                      <th className="text-left py-2 sm:py-3 px-2 text-[10px] sm:text-xs font-semibold text-slate-500 uppercase tracking-wider w-10">#</th>
                      <th className="text-left py-2 sm:py-3 px-2 text-[10px] sm:text-xs font-semibold text-slate-500 uppercase tracking-wider">Patient Name</th>
                      <th className="text-left py-2 sm:py-3 px-2 text-[10px] sm:text-xs font-semibold text-slate-500 uppercase tracking-wider">Email</th>
                      <th className="text-right py-2 sm:py-3 px-2 text-[10px] sm:text-xs font-semibold text-slate-500 uppercase tracking-wider">Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {recentPatients.slice(0, 5).map((patient, index) => {
                      const patientEmail = getDefaultContactValue(patient?.contacts, 'email') || '--';
                      const iconColors = [
                        { bg: 'bg-blue-100', text: 'text-blue-600' },
                        { bg: 'bg-purple-100', text: 'text-purple-600' },
                        { bg: 'bg-pink-100', text: 'text-pink-600' },
                        { bg: 'bg-orange-100', text: 'text-orange-600' },
                        { bg: 'bg-amber-100', text: 'text-amber-600' },
                        { bg: 'bg-cyan-100', text: 'text-cyan-600' },
                        { bg: 'bg-indigo-100', text: 'text-indigo-600' },
                        { bg: 'bg-rose-100', text: 'text-rose-600' },
                        { bg: 'bg-violet-100', text: 'text-violet-600' },
                        { bg: 'bg-sky-100', text: 'text-sky-600' },
                      ];
                      const colorIndex = index % iconColors.length;
                      return (
                        <tr key={patient.id} className="border-b border-slate-50 hover:bg-slate-50 transition-colors">
                          <td className="py-2 sm:py-3 px-2">
                            <span className="text-xs sm:text-sm text-slate-500 font-medium">{index + 1}</span>
                          </td>
                          <td className="py-2 sm:py-3 px-2">
                            <div className="flex items-center gap-2 sm:gap-3">
                              <div className={`w-8 h-8 ${iconColors[colorIndex].bg} rounded-lg flex items-center justify-center flex-shrink-0`}>
                                <User className={`h-4 w-4 ${iconColors[colorIndex].text}`} />
                              </div>
                              <span className="font-medium text-slate-900 text-xs sm:text-sm line-clamp-1">{patient.full_name || patient.name}</span>
                            </div>
                          </td>
                          <td className="py-2 sm:py-3 px-2">
                            <span className="text-slate-600 text-xs sm:text-sm truncate max-w-[200px] block">{patientEmail}</span>
                          </td>
                          <td className="py-2 sm:py-3 px-2 text-right">
                            <Badge className={`capitalize text-[10px] sm:text-xs px-1.5 sm:px-2 py-0.5 ${
                              patient.status === 'active' ? 'bg-emerald-50 text-emerald-700 hover:bg-emerald-50' :
                              patient.status === 'inactive' ? 'bg-slate-100 text-slate-600 hover:bg-slate-100' :
                              'bg-amber-50 text-amber-700 hover:bg-amber-50'
                            }`}>
                              {patient.status || 'Active'}
                            </Badge>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            ) : (
              <div className="text-center py-8">
                <div className="w-12 h-12 rounded-2xl bg-slate-100 flex items-center justify-center mx-auto mb-3">
                  <Users className="h-6 w-6 text-slate-300" />
                </div>
                <p className="text-slate-600 font-medium text-sm">No patients yet</p>
                <p className="text-slate-400 text-xs mt-1">Patients will appear here once added</p>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Recent Activity */}
        <Card className="md:col-span-2 lg:col-span-2 border-slate-200">
          <CardHeader className="pb-3 px-3 sm:px-6">
            <div className="flex items-center justify-between">
              <CardTitle className="flex items-center text-slate-900 font-montserrat font-semibold text-sm sm:text-base">
                <div className="w-7 h-7 sm:w-8 sm:h-8 rounded-lg bg-orange-50 flex items-center justify-center mr-2 sm:mr-3">
                  <TrendingUp className="h-3.5 w-3.5 sm:h-4 sm:w-4 text-orange-600" strokeWidth={2} />
                </div>
                Recent Activity
              </CardTitle>
              <span className="text-[10px] sm:text-xs text-slate-400">Last 7 days</span>
            </div>
          </CardHeader>
          <CardContent className="px-2 sm:px-6">
            {activityLoading ? (
              <div className="space-y-2">
                {[1, 2, 3, 4, 5].map(i => (
                  <div key={i} className="flex items-center gap-3 p-2 rounded-xl">
                    <div className="h-9 w-9 bg-slate-100 rounded-xl animate-pulse" />
                    <div className="flex-1 space-y-1.5">
                      <div className="h-3.5 w-28 bg-slate-200 rounded animate-pulse" />
                      <div className="h-2.5 w-20 bg-slate-100 rounded animate-pulse" />
                    </div>
                    <div className="space-y-1.5 text-right">
                      <div className="h-5 w-14 bg-slate-100 rounded-full animate-pulse" />
                      <div className="h-2.5 w-16 bg-slate-100 rounded animate-pulse" />
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="space-y-1 sm:space-y-2">
                {recentActivity.length > 0 ? (
                  recentActivity.map((activity) => (
                    <div
                      key={activity.id}
                      className="flex items-center gap-2 sm:gap-3 p-2 sm:p-3 rounded-xl hover:bg-slate-50 transition-all group"
                    >
                      {/* Activity Icon */}
                      <div className={`w-8 h-8 sm:w-10 sm:h-10 rounded-xl flex items-center justify-center flex-shrink-0 ${
                        activity.type === 'patient' ? 'bg-teal-50' :
                        activity.type === 'practitioner' ? 'bg-purple-50' :
                        activity.type === 'prescription' ? 'bg-amber-50' :
                        activity.type === 'payment' ? 'bg-emerald-50' :
                        activity.type === 'price_change' ? 'bg-orange-50' :
                        activity.type === 'staff' ? 'bg-blue-50' :
                        'bg-slate-100'
                      }`}>
                        {activity.type === 'patient' ? (
                          <Users className="h-3.5 w-3.5 sm:h-4 sm:w-4 text-teal-600" strokeWidth={2} />
                        ) : activity.type === 'practitioner' ? (
                          <UserCheck className="h-3.5 w-3.5 sm:h-4 sm:w-4 text-purple-600" strokeWidth={2} />
                        ) : activity.type === 'prescription' ? (
                          <Pill className="h-3.5 w-3.5 sm:h-4 sm:w-4 text-amber-600" strokeWidth={2} />
                        ) : activity.type === 'payment' ? (
                          <CreditCard className="h-3.5 w-3.5 sm:h-4 sm:w-4 text-emerald-600" strokeWidth={2} />
                        ) : activity.type === 'price_change' ? (
                          <Tag className="h-3.5 w-3.5 sm:h-4 sm:w-4 text-orange-600" strokeWidth={2} />
                        ) : activity.type === 'staff' ? (
                          <User className="h-3.5 w-3.5 sm:h-4 sm:w-4 text-blue-600" strokeWidth={2} />
                        ) : (
                          <BarChart3 className="h-3.5 w-3.5 sm:h-4 sm:w-4 text-slate-500" strokeWidth={2} />
                        )}
                      </div>
                      
                      {/* Activity Info */}
                      <div className="flex-1 min-w-0">
                        <p className="font-medium text-slate-800 text-xs sm:text-sm truncate">{activity.action}</p>
                        <p className="text-[10px] sm:text-xs text-slate-500 truncate">{activity.entity}</p>
                      </div>
                      
                      {/* Time & Badge */}
                      <div className="flex flex-col items-end gap-0.5 sm:gap-1">
                        <Badge className={`capitalize text-[10px] sm:text-xs px-1.5 sm:px-2 py-0.5 ${
                          activity.type === 'patient' ? 'bg-teal-50 text-teal-700 hover:bg-teal-50' :
                          activity.type === 'practitioner' ? 'bg-purple-50 text-purple-700 hover:bg-purple-50' :
                          activity.type === 'prescription' ? 'bg-amber-50 text-amber-700 hover:bg-amber-50' :
                          activity.type === 'payment' ? 'bg-emerald-50 text-emerald-700 hover:bg-emerald-50' :
                          activity.type === 'price_change' ? 'bg-orange-50 text-orange-700 hover:bg-orange-50' :
                          activity.type === 'staff' ? 'bg-blue-50 text-blue-700 hover:bg-blue-50' :
                          'bg-white text-foreground hover:bg-white border border-border'
                        }`}>
                          {activity.type === 'price_change' ? 'Price' : activity.type}
                        </Badge>
                        <span className="text-[10px] sm:text-xs text-slate-400">{activity.time}</span>
                      </div>
                    </div>
                  ))
                ) : (
                  <div className="text-center py-8 sm:py-10">
                    <div className="w-12 h-12 sm:w-14 sm:h-14 rounded-2xl bg-slate-100 flex items-center justify-center mx-auto mb-3 sm:mb-4">
                      <TrendingUp className="h-6 w-6 sm:h-7 sm:w-7 text-slate-300" strokeWidth={1.5} />
                    </div>
                    <p className="text-slate-600 font-medium text-xs sm:text-sm">No recent activities</p>
                    <p className="text-slate-400 text-[10px] sm:text-xs mt-1">Activity will appear as changes are made</p>
                  </div>
                )}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
      {/* Task Management */}
      {user?.practitionerId && (
        <TaskPanel providerId={user.practitionerId} clinicId={clinic?.id} />
      )}
    </div>
  );
};

export default ClinicDashboard;
